import React, { useState, useEffect } from "react";
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useVendorAuth } from '../../context/VendorAuthContext';
import { setSessionWithExpiry } from '../helpers/sessionUtils';
import SEO from "../../components/common/SEO";

const LOGIN_RESOURCES = [
    { type: 'link', rel: 'stylesheet', href: '/assets/vendor/font-awesome/css/fontawesome-all.min.css', id: 'login-page-fa' },
    { type: 'link', rel: 'preconnect', href: 'https://fonts.googleapis.com', id: 'login-page-gfont-preconnect1' },
    { type: 'link', rel: 'preconnect', href: 'https://fonts.gstatic.com', crossOrigin: 'true', id: 'login-page-gfont-preconnect2' },
    { type: 'link', rel: 'stylesheet', href: 'https://fonts.googleapis.com/css2?family=Poppins:ital,wght=0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900&display=swap', id: 'login-page-gfont-style' }
];

const FormMessageDisplay = ({ message, type }) => {
    if (!message) return null;
    const alertStyle = {
        padding: '12px 16px',
        borderRadius: '8px',
        fontSize: '0.9rem',
        fontWeight: '500',
        marginBottom: '20px',
        textAlign: 'left',
        border: '1px solid transparent',
    };
    const style = type === 'error' ? {
        ...alertStyle,
        backgroundColor: '#fee2e2',
        color: '#ef4444',
        borderColor: '#fca5a5'
    } : (type === 'success' ? {
        ...alertStyle,
        backgroundColor: '#e6f9f3',
        color: '#0bc184',
        borderColor: '#a7f3d0'
    } : {
        ...alertStyle,
        backgroundColor: '#eff6ff',
        color: '#25a5f0',
        borderColor: '#bfdbfe'
    });
    return (<div style={style} role="alert">{message}</div>);
};

function Login() {
    const navigate = useNavigate();
    const { login } = useVendorAuth();

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [formMessage, setFormMessage] = useState({ text: '', type: '' });

    useEffect(() => {
        document.title = "Login - Mangoloops";
        LOGIN_RESOURCES.forEach(attrs => {
            if (!document.getElementById(attrs.id)) {
                const el = document.createElement(attrs.type);
                Object.keys(attrs).forEach(key => { if (key !== 'type') el[key] = attrs[key]; });
                document.head.appendChild(el);
            }
        });
        return () => {
            LOGIN_RESOURCES.forEach(attrs => {
                const el = document.getElementById(attrs.id);
                if (el) el.remove();
            });
        };
    }, []);

    const handleLogin = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setFormMessage({ text: '', type: '' });

        if (!email || !password) {
            setFormMessage({ text: "Please enter email and password.", type: "error" });
            setIsLoading(false);
            return;
        }

        try {
            const apiUrl = `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api'}/vendors/login`;
            const response = await axios.post(apiUrl, { email, password });
            const { user, token, message } = response.data;

            setFormMessage({ text: message || "Login successful!", type: "success" });
            setSessionWithExpiry(response.data);
            login(token, user);
        } catch (err) {
            const errorData = err.response?.data;
            const errorMessage = errorData?.message || "Login failed. Please check your credentials.";

            if (errorData?.status === 'not_verified_redirect_to_registration') {
                setFormMessage({ text: errorMessage, type: "info" });
                setTimeout(() => navigate(`/admin/kyc-verification`), 2000);
            } else {
                setFormMessage({ text: errorMessage, type: "error" });
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <style>{`
                :root {
                    --primary: #4f46e5;
                    --primary-hover: #4338ca;
                    --secondary-blue: #25a5f0;
                    --secondary-blue-hover: #1d8fd6;
                    --success: #0bc184;
                    --danger: #ef4444;
                    --border-color: #cbd5e1;
                    --text-dark: #1f2937;
                    --text-muted: #6b7280;
                    --bg-color: #f8fafc;
                }

                .login-page-body {
                    font-family: 'Segoe UI', system-ui, -apple-system, sans-serif;
                    background-color: var(--bg-color);
                    color: var(--text-dark);
                    margin: 0;
                    padding: 20px; 
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    min-height: 100vh;
                    box-sizing: border-box;
                }

                .portal-wrapper {
                    width: 100%;
                    max-width: 460px;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                }

                .logo-container {
                    margin-bottom: 22px; 
                    text-align: center;
                }

                .logo-container img {
                    max-width: 210px; 
                    height: auto;
                    display: block;
                }

                .auth-card {
                    background: #ffffff;
                    width: 100%;
                    padding: 35px; 
                    border-radius: 16px;
                    box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.05), 0 8px 10px -6px rgba(0, 0, 0, 0.05);
                    border: 1px solid #e5e7eb;
                    box-sizing: border-box;
                }

                .header h2 {
                    font-size: 1.85rem;
                    margin: 0 0 6px 0;
                    font-weight: 700;
                    color: #111827;
                }

                .header p {
                    color: var(--text-muted);
                    margin-bottom: 26px; 
                    font-size: 0.95rem;
                    line-height: 1.4;
                }

                .form-group {
                    margin-bottom: 20px;
                    position: relative;
                }

                input[type="email"], input[type="password"] {
                    width: 100%;
                    padding: 14px;
                    border: 1px solid var(--border-color);
                    border-radius: 8px;
                    box-sizing: border-box;
                    font-size: 1rem;
                    color: var(--text-dark);
                    transition: all 0.2s;
                }

                input:focus {
                    outline: none;
                    border-color: var(--primary);
                    box-shadow: 0 0 0 4px rgba(79, 70, 229, 0.12);
                }

                .forgot-link {
                    text-align: right;
                    display: block;
                    margin-top: -10px;
                    margin-bottom: 25px;
                    color: var(--secondary-blue);
                    text-decoration: none;
                    font-weight: 500;
                    font-size: 0.9rem;
                }
                .forgot-link:hover { text-decoration: underline; }

                .btn {
                    width: 100%;
                    border: none;
                    padding: 14px;
                    font-size: 1rem;
                    font-weight: 600;
                    border-radius: 8px;
                    cursor: pointer;
                    transition: background 0.2s, opacity 0.2s;
                    box-sizing: border-box;
                }

                .btn-gray-login {
                    background-color: #828282; 
                    color: white;
                }
                .btn-gray-login:hover { background-color: #6b6b6b; }

                .btn-blue-action {
                    background-color: var(--secondary-blue);
                    color: white;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 10px;
                }
                .btn-blue-action:hover { background-color: var(--secondary-blue-hover); }

                .or-separator {
                    display: flex;
                    align-items: center;
                    text-align: center;
                    color: var(--text-muted);
                    margin: 24px 0;
                    font-size: 0.9rem;
                }
                .or-separator::before, .or-separator::after {
                    content: '';
                    flex: 1;
                    border-bottom: 1px solid #e2e8f0;
                }
                .or-separator:not(:empty)::before { margin-right: .7em; }
                .or-separator:not(:empty)::after { margin-left: .7em; }

                .footer-switch-text {
                    text-align: center;
                    margin-top: 25px;
                    font-size: 0.95rem;
                    color: var(--text-dark);
                }
                .footer-switch-text a {
                    color: var(--secondary-blue);
                    text-decoration: none;
                    font-weight: 600;
                    cursor: pointer;
                }
                .footer-switch-text a:hover { text-decoration: underline; }
            `}</style>
            <SEO title="Vendor Login | Mangoloops" description="Login as a vendor on Mangoloops to list your hotels, cabs, and holiday packages." />

            <div className="login-page-body">
                <div className="portal-wrapper">

                    <div className="logo-container">
                        <Link to="/">
                            <img src="/logo-mangoloops.png" alt="MangoLoops Logo" />
                        </Link>
                    </div>

                    <div className="auth-card">
                        <div id="loginPassPanel" className="auth-panel active">
                            <div className="header">
                                <h2>Welcome back!</h2>
                                <p>Start managing your finance faster and better</p>
                            </div>

                            <FormMessageDisplay message={formMessage.text} type={formMessage.type} />

                            <form onSubmit={handleLogin}>
                                <div className="form-group">
                                    <input
                                        type="email"
                                        placeholder="Email address"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        required
                                        disabled={isLoading}
                                    />
                                </div>
                                <div className="form-group">
                                    <input
                                        type="password"
                                        placeholder="Password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                        disabled={isLoading}
                                    />
                                </div>

                                <Link to="/forgot-password" className="forgot-link">Forgot Password?</Link>

                                <button type="submit" className="btn btn-gray-login" disabled={isLoading}>
                                    {isLoading ? 'Logging in...' : 'Login'}
                                </button>
                            </form>

                            <div className="or-separator">or</div>

                            <button type="button" className="btn btn-blue-action" onClick={() => navigate('/login-otp')}>
                                🔒 Login with Mobile Number
                            </button>

                            <div className="footer-switch-text">
                                Don’t have an account? <Link to="/registration">Sign Up</Link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default Login;
